import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiGet, apiPost } from "../../lib/api";
import { ClientInstallPrompt } from "../../components/client/ClientInstallPrompt";
import { SUPPORTED_LOCALES, type SupportedLocale } from "../../lib/locales";

type ActionDetails = {
  action: "confirm" | "cancel" | "reschedule";
  appointment_id: string;
  tenant_id: string;
  locale: SupportedLocale;
  branch_name?: string;
  service_name?: string;
  staff_name?: string;
  customer_name?: string;
  status: string;
  start_at: string;
  end_at: string;
  expires_at: string;
  can_reschedule: boolean;
};

type AppLocale = SupportedLocale;

function normalizeLocale(value: string | null | undefined): AppLocale | null {
  const normalized = (value || "").toLowerCase().split("-")[0];
  if (SUPPORTED_LOCALES.includes(normalized as AppLocale)) return normalized as AppLocale;
  return null;
}

function ActionView({ mode }: { mode: "confirm" | "manage" }) {
  const { token = "" } = useParams();
  const [searchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const tr = (key: string, fallback: string, options?: Record<string, unknown>) => {
    const translated = t(key, { ...options, defaultValue: fallback });
    return translated === key ? fallback : translated;
  };
  const localeInitializedRef = useRef(false);
  const [newStartAt, setNewStartAt] = useState("");
  const [selectedLocale, setSelectedLocale] = useState<AppLocale>("en");
  const [result, setResult] = useState("");

  const details = useQuery({
    queryKey: ["client-action-detail", token],
    queryFn: () => apiGet<ActionDetails>(`/v3/public/actions/${token}`, { auth: "none" }),
    enabled: !!token,
  });

  const localeStorageKey = useMemo(
    () => `client_locale:${details.data?.tenant_id || "action"}`,
    [details.data?.tenant_id]
  );

  useEffect(() => {
    const urlLocale = normalizeLocale(searchParams.get("lang"));
    const storedLocale = normalizeLocale(localStorage.getItem(localeStorageKey));
    const detailsLocale = normalizeLocale(details.data?.locale);
    if (!localeInitializedRef.current || urlLocale) {
      const nextLocale = urlLocale || storedLocale || detailsLocale || "en";
      setSelectedLocale(nextLocale);
      localeInitializedRef.current = true;
    }
  }, [searchParams, localeStorageKey, details.data?.locale]);

  useEffect(() => {
    i18n.changeLanguage(selectedLocale);
    localStorage.setItem(localeStorageKey, selectedLocale);
  }, [selectedLocale, localeStorageKey, i18n]);

  const apply = useMutation({
    mutationFn: () =>
      apiPost(
        "/v3/public/actions",
        {
          token,
          new_start_at: mode === "manage" && newStartAt ? new Date(newStartAt).toISOString() : undefined,
          locale: selectedLocale,
        },
        { auth: "none" }
      ),
    onSuccess: (data) => setResult(JSON.stringify(data, null, 2)),
    onError: (err) => setResult(String(err)),
  });

  return (
    <div className="client-container">
      <section className="panel p-4 sm:p-5">
        <div className="flex justify-end">
          <select
            className="input w-full max-w-[180px] bg-white/90"
            aria-label="Action page language"
            value={selectedLocale}
            onChange={(e) => setSelectedLocale(e.target.value as AppLocale)}
          >
            {SUPPORTED_LOCALES.map((locale) => (
              <option key={locale} value={locale}>
                {tr(`locale.${locale}`, locale.toUpperCase())}
              </option>
            ))}
          </select>
        </div>
        <h1 className="mt-3 text-xl font-semibold sm:text-2xl">
          {mode === "confirm"
            ? tr("client.actions.confirmTitle", "Confirm Appointment")
            : tr("client.actions.manageTitle", "Manage Appointment")}
        </h1>
        {details.isLoading ? <p className="mt-2 text-sm">{tr("client.actions.loading", "Loading details...")}</p> : null}
        {details.error ? <p className="mt-2 text-sm text-red-600">{String(details.error)}</p> : null}
        {details.data ? (
          <div className="mt-3 grid gap-1 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
            <p>
              <strong>{details.data.customer_name || tr("client.actions.defaultClient", "Client")}</strong>
            </p>
            <p>
              {details.data.service_name || tr("client.actions.defaultService", "Service")} {tr("client.actions.withStaff", "with")}{" "}
              {details.data.staff_name || tr("client.actions.defaultStaff", "Staff")}
            </p>
            <p>{new Date(details.data.start_at).toLocaleString()}</p>
            <p className="text-slate-500">
              {tr("client.actions.tokenAction", "Token action")}: {details.data.action}
            </p>
          </div>
        ) : null}
        {mode === "manage" && details.data?.can_reschedule ? (
          <input className="input mt-3" type="datetime-local" value={newStartAt} onChange={(e) => setNewStartAt(e.target.value)} />
        ) : null}
        <button className="btn mt-3 w-full sm:w-auto" onClick={() => apply.mutate()} disabled={!token}>
          {tr("client.actions.apply", "Apply action")}
        </button>
        {result ? <pre className="mt-3 overflow-x-auto rounded bg-slate-100 p-2 text-xs">{result}</pre> : null}
        <div className="mt-3">
          <ClientInstallPrompt />
        </div>
      </section>
    </div>
  );
}

export function ClientConfirmPage() {
  return <ActionView mode="confirm" />;
}

export function ClientManagePage() {
  return <ActionView mode="manage" />;
}
